/**
 * Shared vocabulary for the property and incentive sides of matching.
 *
 * These IDs are stored directly in intake JSON, property settings, and
 * requirements vectors. Labels are presentation only and must not be sent to
 * the matcher in place of the IDs.
 */
/**
 * A value in a matching vocabulary.
 *
 * `storable` = offered as a fact about a property.
 * `authorable` = offered as a requirement on an incentive.
 *
 * The two differ on purpose (IMPL-002 D2): a property may legitimately hold a
 * value that no incentive ever gates on, but an incentive must never gate on a
 * value no property can hold. Anything already saved on a record stays
 * editable regardless — see `optionsIncludingSelected`.
 */
export interface IMatchingVocabularyOption {
  id: string;
  label: string;
  storable: boolean;
  authorable: boolean;
  /** Why this value is not offered on one side. Empty when it is offered on both. */
  note: string;
}

const NOT_INCENTIVE_LANGUAGE =
  "Not used in incentive language; storable on a property only.";

export const BUILDING_USE_OPTIONS = [
  { id: "commercial", label: "Commercial", storable: true, authorable: true, note: "" },
  { id: "residential", label: "Residential", storable: true, authorable: true, note: "" },
  { id: "multifamily", label: "Multifamily", storable: true, authorable: true, note: "" },
  { id: "mixed_use", label: "Mixed use", storable: true, authorable: true, note: "" },
  { id: "office", label: "Office", storable: true, authorable: true, note: "" },
  { id: "retail", label: "Retail", storable: true, authorable: true, note: "" },
  { id: "hospital", label: "Hospital", storable: true, authorable: true, note: "" },
  { id: "school", label: "School", storable: true, authorable: true, note: "" },
  { id: "higher_education", label: "Higher education", storable: true, authorable: true, note: "" },
  { id: "religious_worship", label: "Religious worship", storable: true, authorable: true, note: "" },
  { id: "industrial", label: "Industrial", storable: true, authorable: false, note: NOT_INCENTIVE_LANGUAGE },
  { id: "warehouse", label: "Warehouse", storable: true, authorable: false, note: NOT_INCENTIVE_LANGUAGE },
  { id: "hotel", label: "Hotel", storable: true, authorable: false, note: NOT_INCENTIVE_LANGUAGE },
  {
    id: "other",
    label: "Other",
    storable: true,
    authorable: false,
    note: "Must never satisfy an eligible-use list.",
  },
] as const satisfies readonly IMatchingVocabularyOption[];

export type BuildingUseId = (typeof BUILDING_USE_OPTIONS)[number]["id"];

export const BUILDING_USE_IDS: BuildingUseId[] = BUILDING_USE_OPTIONS.map(
  (option) => option.id,
);

export const BUILDING_USE_LABELS: Record<string, string> =
  Object.fromEntries(
    BUILDING_USE_OPTIONS.map((option) => [option.id, option.label]),
  );

/**
 * Applicant identity — who is applying — for BOTH sides of matching.
 *
 * One list, not two (IMPL-002 R2). A property's `OwnerClass` and an
 * incentive's `eligibleApplicantClasses` are compared by exact set membership
 * in `IncentiveRequirementsMatcher.MatchesApplicantClass`, so a value that
 * exists on only one side is dead by construction.
 *
 * The mixed casing is deliberate: the matcher compares case-insensitively but
 * these exact spellings are already stored in vectors and property settings.
 * Do not tidy them.
 */
const PENDING_DIMENSION_REVIEW =
  "Not an owner identity. Pending the IMPL-002 R3b dimension review; kept so existing vectors still render.";

export const APPLICANT_CLASS_VOCABULARY = [
  { id: "private", label: "Private owner", storable: true, authorable: true, note: "" },
  { id: "commercial", label: "Commercial owner", storable: true, authorable: true, note: "" },
  { id: "nonprofit", label: "Nonprofit", storable: true, authorable: true, note: "" },
  { id: "government", label: "Government", storable: true, authorable: true, note: "" },
  {
    id: "PublicAuthority",
    label: "Public authority",
    storable: true,
    authorable: true,
    note: "Distinct from Government. A program open to both must list both.",
  },
  {
    id: "PublicHousingAuthority",
    label: "Public housing authority",
    storable: true,
    authorable: true,
    note: "Does not imply Public authority.",
  },
  { id: "homeowner", label: "Homeowner", storable: true, authorable: true, note: "" },
  { id: "developer", label: "Developer", storable: true, authorable: true, note: "" },
  { id: "School", label: "School district / K-12", storable: true, authorable: true, note: "" },
  { id: "Hospital", label: "Hospital system", storable: true, authorable: true, note: "" },
  { id: "SUNY", label: "SUNY / state university", storable: true, authorable: true, note: "" },
  { id: "StateFacility", label: "State facility", storable: true, authorable: true, note: "" },
  { id: "PublicEntity", label: "Public entity", storable: true, authorable: true, note: "" },
  {
    id: "RuralCommunity",
    label: "Rural community",
    storable: false,
    authorable: false,
    note: `${PENDING_DIMENSION_REVIEW} This is a jurisdiction fact, not an owner.`,
  },
  {
    id: "Campus",
    label: "Campus",
    storable: false,
    authorable: false,
    note: `${PENDING_DIMENSION_REVIEW} This is a site type, not an owner.`,
  },
] as const satisfies readonly IMatchingVocabularyOption[];

export type ApplicantClassId =
  (typeof APPLICANT_CLASS_VOCABULARY)[number]["id"];

export const APPLICANT_CLASS_IDS: ApplicantClassId[] =
  APPLICANT_CLASS_VOCABULARY.map((option) => option.id);

export const APPLICANT_CLASS_LABELS: Record<string, string> =
  Object.fromEntries(
    APPLICANT_CLASS_VOCABULARY.map((option) => [option.id, option.label]),
  );

export const APPLICANT_CLASS_NOTES: Record<string, string> =
  Object.fromEntries(
    APPLICANT_CLASS_VOCABULARY.filter((option) => option.note).map((option) => [
      option.id,
      option.note,
    ]),
  );

/** Offered as a property's OwnerClass. */
export const OWNER_CLASS_OPTIONS: string[] = APPLICANT_CLASS_VOCABULARY.filter(
  (option) => option.storable,
).map((option) => option.id);

/** Offered on an incentive's eligible / excluded applicant lists. */
export const AUTHORABLE_APPLICANT_CLASS_IDS: string[] =
  APPLICANT_CLASS_VOCABULARY.filter((option) => option.authorable).map(
    (option) => option.id,
  );

/** Offered on an incentive's eligible / excluded building use lists. */
export const AUTHORABLE_BUILDING_USE_IDS: string[] =
  BUILDING_USE_OPTIONS.filter((option) => option.authorable).map(
    (option) => option.id,
  );

export type OwnerClass = ApplicantClassId;

export const OWNER_CLASS_LABELS: Record<string, string> =
  APPLICANT_CLASS_LABELS;

export const parseBuildingUses = (value?: string | null): string[] => {
  if (!value?.trim()) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return normalizeBuildingUses(parsed);
  } catch {
    return [];
  }
};

export const normalizeBuildingUses = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (item, index, all): item is BuildingUseId =>
      typeof item === "string" &&
      BUILDING_USE_IDS.includes(item as BuildingUseId) &&
      all.indexOf(item) === index,
  );
};

export const serializeBuildingUses = (values: string[]): string =>
  JSON.stringify(
    values.filter((value, index, all) =>
      BUILDING_USE_IDS.includes(value as BuildingUseId) &&
      all.indexOf(value) === index,
    ),
  );
